/**
 * Step0GoalOfferCard
 * Step 0 of the plan workflow - Pick the outcome (goal) and the offer (CTA style)
 */

import React, { useState } from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Target,
  Gift,
  ChevronDown,
  ChevronUp,
  Check,
  Plus,
  Pencil,
  Loader2
} from 'lucide-react';

// Preset options
const PRESET_GOALS = [
  { id: 'signup', label: 'Signup', icon: '📝' },
  { id: 'purchase', label: 'Purchase', icon: '💳' },
  { id: 'demo', label: 'Demo', icon: '🎥' },
  { id: 'lead', label: 'Lead', icon: '📧' },
];

const PRESET_OFFERS = [
  { id: 'start', label: 'Start', icon: '🚀' },
  { id: 'get_pricing', label: 'Get pricing', icon: '💰' },
  { id: 'book', label: 'Book', icon: '📅' },
  { id: 'try_free', label: 'Try free', icon: '✨' },
  { id: 'download', label: 'Download', icon: '⬇️' },
];

export default function Step0GoalOfferCard({
  stepData,
  customOptions = {},
  isCompleted = false,
  onSave,
  onEdit,
  onAddCustomOption,
  saving = false
}) {
  const [primaryGoal, setPrimaryGoal] = useState(stepData?.primary_goal || null);
  const [customPrimaryGoal, setCustomPrimaryGoal] = useState(stepData?.custom_primary_goal || '');
  const [offerStyle, setOfferStyle] = useState(stepData?.offer_style || null);
  const [customOfferStyle, setCustomOfferStyle] = useState(stepData?.custom_offer_style || '');
  const [budgetRange, setBudgetRange] = useState(stepData?.budget_range || '');
  const [geo, setGeo] = useState(stepData?.geo || '');
  const [timeline, setTimeline] = useState(stepData?.timeline || '');
  const [showOptional, setShowOptional] = useState(false);
  const [addingCustomGoal, setAddingCustomGoal] = useState(false);
  const [addingCustomOffer, setAddingCustomOffer] = useState(false);
  const [newCustomGoal, setNewCustomGoal] = useState('');
  const [newCustomOffer, setNewCustomOffer] = useState('');

  // Combine presets with custom options
  const allGoals = [
    ...PRESET_GOALS,
    ...(customOptions.primary_goals || []).map(g => ({ id: `custom_${g}`, label: g, icon: '⭐', isCustom: true }))
  ];

  const allOffers = [
    ...PRESET_OFFERS,
    ...(customOptions.offer_styles || []).map(o => ({ id: `custom_${o}`, label: o, icon: '⭐', isCustom: true }))
  ];

  const handleSave = () => {
    if (!primaryGoal || !offerStyle) {
      return;
    }

    const data = {
      primary_goal: primaryGoal,
      custom_primary_goal: primaryGoal.startsWith('custom_') ? customPrimaryGoal : null,
      offer_style: offerStyle,
      custom_offer_style: offerStyle.startsWith('custom_') ? customOfferStyle : null,
      budget_range: budgetRange,
      geo: geo,
      timeline: timeline
    };

    onSave(data);
  };

  const handleAddCustomGoal = () => {
    if (newCustomGoal.trim()) {
      onAddCustomOption('goal', newCustomGoal.trim());
      setPrimaryGoal(`custom_${newCustomGoal.trim()}`);
      setCustomPrimaryGoal(newCustomGoal.trim());
      setNewCustomGoal('');
      setAddingCustomGoal(false);
    }
  };

  const handleAddCustomOffer = () => {
    if (newCustomOffer.trim()) {
      onAddCustomOption('offer', newCustomOffer.trim());
      setOfferStyle(`custom_${newCustomOffer.trim()}`);
      setCustomOfferStyle(newCustomOffer.trim());
      setNewCustomOffer('');
      setAddingCustomOffer(false);
    }
  };

  const getGoalLabel = (goalId) => {
    const goal = allGoals.find(g => g.id === goalId);
    return goal ? goal.label : goalId?.replace('custom_', '');
  };

  const getOfferLabel = (offerId) => {
    const offer = allOffers.find(o => o.id === offerId);
    return offer ? offer.label : offerId?.replace('custom_', '');
  };

  // Completed state - show summary
  if (isCompleted) {
    return (
      <div className="bg-surface border border-green-200 dark:border-green-800 rounded-xl p-6" data-testid="step0-completed">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center">
              <Check className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">Step 0 — Goal & Offer Defined</h3>
              <div className="text-sm text-muted-foreground mt-1 space-y-0.5">
                <p><span className="font-medium">Goal:</span> {getGoalLabel(stepData?.primary_goal)}</p>
                <p><span className="font-medium">Offer:</span> {getOfferLabel(stepData?.offer_style)}</p>
                {stepData?.budget_range && <p><span className="font-medium">Budget:</span> {stepData.budget_range}</p>}
                {stepData?.geo && <p><span className="font-medium">Geo:</span> {stepData.geo}</p>}
                {stepData?.timeline && <p><span className="font-medium">Timeline:</span> {stepData.timeline}</p>}
              </div>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onEdit} className="text-muted-foreground">
            <Pencil className="w-4 h-4 mr-1" />
            Edit
          </Button>
        </div>
      </div>
    );
  }

  // Active state - show form
  return (
    <div className="bg-surface border border-purple-200 dark:border-purple-800 rounded-xl p-6" data-testid="step0-active">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-full bg-purple-500/10 flex items-center justify-center">
          <Target className="w-5 h-5 text-purple-500" />
        </div>
        <div>
          <h3 className="font-semibold text-foreground">Step 0 — Pick the outcome and the offer</h3>
          <p className="text-sm text-muted-foreground">Define what you want visitors to do</p>
        </div>
      </div>

      <div className="space-y-6">
        {/* Primary Goal Selection */}
        <div className="space-y-3">
          <Label className="flex items-center gap-2">
            <Target className="w-4 h-4 text-purple-500" />
            Primary Goal *
          </Label>
          <div className="flex flex-wrap gap-2">
            {allGoals.map((goal) => (
              <button
                key={goal.id}
                onClick={() => {
                  setPrimaryGoal(goal.id);
                  if (goal.isCustom) {
                    setCustomPrimaryGoal(goal.label);
                  }
                }}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  primaryGoal === goal.id
                    ? 'bg-purple-500 text-white'
                    : 'bg-muted hover:bg-muted/80 text-foreground'
                }`}
                data-testid={`goal-${goal.id}`}
              >
                <span className="mr-1.5">{goal.icon}</span>
                {goal.label}
              </button>
            ))}
            
            {/* Add Custom Goal */}
            {addingCustomGoal ? (
              <div className="flex items-center gap-2">
                <Input
                  value={newCustomGoal}
                  onChange={(e) => setNewCustomGoal(e.target.value)}
                  placeholder="Enter custom goal..."
                  className="w-40 h-9"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleAddCustomGoal();
                    if (e.key === 'Escape') setAddingCustomGoal(false);
                  }}
                />
                <Button size="sm" onClick={handleAddCustomGoal} className="h-9">
                  <Check className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <button
                onClick={() => setAddingCustomGoal(true)}
                className="px-4 py-2 rounded-full text-sm font-medium bg-muted/50 hover:bg-muted text-muted-foreground border border-dashed border-muted-foreground/30"
              >
                <Plus className="w-4 h-4 inline mr-1" />
                Add custom
              </button>
            )}
          </div>
        </div>

        {/* Offer Style Selection */}
        <div className="space-y-3">
          <Label className="flex items-center gap-2">
            <Gift className="w-4 h-4 text-purple-500" />
            Offer / CTA Style *
          </Label>
          <div className="flex flex-wrap gap-2">
            {allOffers.map((offer) => (
              <button
                key={offer.id}
                onClick={() => {
                  setOfferStyle(offer.id);
                  if (offer.isCustom) {
                    setCustomOfferStyle(offer.label);
                  }
                }}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  offerStyle === offer.id
                    ? 'bg-purple-500 text-white'
                    : 'bg-muted hover:bg-muted/80 text-foreground'
                }`}
                data-testid={`offer-${offer.id}`}
              >
                <span className="mr-1.5">{offer.icon}</span>
                {offer.label}
              </button>
            ))}
            
            {/* Add Custom Offer */}
            {addingCustomOffer ? (
              <div className="flex items-center gap-2">
                <Input
                  value={newCustomOffer}
                  onChange={(e) => setNewCustomOffer(e.target.value)}
                  placeholder="Enter custom offer..."
                  className="w-40 h-9"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleAddCustomOffer();
                    if (e.key === 'Escape') setAddingCustomOffer(false);
                  }}
                />
                <Button size="sm" onClick={handleAddCustomOffer} className="h-9">
                  <Check className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <button
                onClick={() => setAddingCustomOffer(true)}
                className="px-4 py-2 rounded-full text-sm font-medium bg-muted/50 hover:bg-muted text-muted-foreground border border-dashed border-muted-foreground/30"
              >
                <Plus className="w-4 h-4 inline mr-1" />
                Add custom
              </button>
            )}
          </div>
        </div>

        {/* Optional Fields Toggle */}
        <button
          onClick={() => setShowOptional(!showOptional)}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          {showOptional ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          Optional: Budget, Geo, Timeline
        </button>

        {/* Optional Fields */}
        {showOptional && (
          <div className="space-y-4 pl-4 border-l-2 border-muted">
            <div className="space-y-2">
              <Label htmlFor="budget">Budget Range</Label>
              <Input
                id="budget"
                value={budgetRange}
                onChange={(e) => setBudgetRange(e.target.value)}
                placeholder="e.g., $5,000 - $10,000"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="geo">Geographic Target</Label>
              <Input
                id="geo"
                value={geo}
                onChange={(e) => setGeo(e.target.value)}
                placeholder="e.g., US, Boston, Europe"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="timeline">Timeline</Label>
              <Input
                id="timeline"
                value={timeline}
                onChange={(e) => setTimeline(e.target.value)}
                placeholder="e.g., 2 weeks, Q1 2025"
              />
            </div>
          </div>
        )}

        {/* Save Button */}
        <div className="pt-4 border-t">
          <Button
            onClick={handleSave}
            disabled={!primaryGoal || !offerStyle || saving}
            className="bg-purple-500 hover:bg-purple-600 text-white"
            data-testid="save-step0-btn"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Check className="w-4 h-4 mr-2" />
                Save Goal
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
